import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut, type User as FirebaseUser } from 'firebase/auth';
import type { User, AuthState, UserRole } from '../types/user';
import { auth } from '../lib/firebase';
import { tenantLookup } from '../lib/auth/tenantLookup';
import { readClaims, roleFromClaim } from '../lib/auth/claims';
import { getMe } from '../lib/api/endpoints';

interface AuthContextValue extends AuthState {
  /** true mientras se resuelve si hay una sesión de Firebase persistida (F5, pestaña nueva). */
  isInitializing: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  /** Solo existe en build de desarrollo — el rol real viene firmado en el token. */
  switchRole?: (role: UserRole) => void;
  /**
   * Atajo SOLO de desarrollo: entra con un usuario de prueba sin tocar
   * Firebase para nada (ni tenant-lookup, ni sign-in, ni GET /me). Existe
   * porque hoy no hay apiKey real (BE-25) ni confirmación de que las
   * cuentas demo existan en Identity Platform — ver PENDIENTES.md, FE-01.
   * `import.meta.env.DEV` es una constante estática: Vite elimina esta
   * rama por completo en el build de producción, igual que `switchRole`.
   */
  loginDev?: (role: UserRole) => void;
  updateProfile: (data: Partial<Pick<User, 'name' | 'email' | 'phone'>>) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Usuarios de prueba para `loginDev` — mismos datos que el antiguo
// MOCK_USERS que este archivo reemplazó. Alcance por rol: consolidado
// (sin sub-unidades) para admin/executive, acotado a 'su1' para
// supervisor/operator, para que el filtro de FE-03 también sea probable.
const DEV_FIXTURES: Record<UserRole, User> = {
  supervisor: { id: 'dev-u1', name: 'Diego Torres', email: 'supervisor@demo.com', role: 'supervisor', tenantId: 't1', subunitIds: ['su1'] },
  executive: { id: 'dev-u2', name: 'Valentina Cruz', email: 'ejecutivo@demo.com', role: 'executive', tenantId: 't1', subunitIds: [] },
  admin: { id: 'dev-u3', name: 'Roberto Pinto', email: 'admin@demo.com', role: 'admin', tenantId: 't1', subunitIds: [] },
  operator: { id: 'dev-u4', name: 'Carlos Mendoza', email: 'operador@demo.com', role: 'operator', tenantId: 't1', subunitIds: ['su1'] },
};

/**
 * Arma el `User` de dominio combinando: los claims firmados por el backend
 * (rol, tenant, sub-unidades — solo para UI, docs/Paquete_Arranque_Frontend.md
 * §4) y `GET /me` (perfil + alcance ya resuelto, mismo doc §3 paso 7).
 */
async function buildAuthenticatedState(firebaseUser: FirebaseUser): Promise<{ user: User; token: string }> {
  const idTokenResult = await firebaseUser.getIdTokenResult();
  const claims = readClaims(idTokenResult);
  // auth.currentUser ya es firebaseUser en este punto (recién autenticado o
  // recién rehidratado por onAuthStateChanged) — getMe() puede resolver su
  // propio token internamente sin que se lo pasemos.
  const me = await getMe();

  const user: User = {
    id: me.userId,
    name: me.displayName,
    email: me.email ?? firebaseUser.email ?? '',
    // #137: el rol de la UI sale de GET /me (la fila, siempre fresca), no del
    // claim del token (hasta ~1 h viejo tras un cambio de rol). El core ya
    // autoriza con la fila desde #127, así que el claim solo pintaba acciones
    // que daban 403 u ocultaba las que ya se podían. Se cae al claim solo si
    // me.role llegara con un valor desconocido.
    role: roleFromClaim(me.role) ?? claims.role,
    tenantId: claims.tenantId || me.tenantId,
    subunitIds: me.firestore?.subunitIds ?? claims.subunitIds,
    firestoreCollections: me.firestore?.vehiclesCollection && me.firestore?.alertsCollection
      ? { vehicles: me.firestore.vehiclesCollection, alerts: me.firestore.alertsCollection }
      : undefined,
  };

  return { user, token: idTokenResult.token };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: false,
  });
  const [isInitializing, setIsInitializing] = useState(true);

  // Rehidrata la sesión al montar (F5, pestaña nueva) — sin esto, un usuario
  // con sesión válida vería el login por una fracción de segundo, o el
  // router lo expulsaría antes de que Firebase resuelva.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async firebaseUser => {
      if (!firebaseUser) {
        setState({ user: null, token: null, isAuthenticated: false, isLoading: false });
        setIsInitializing(false);
        return;
      }
      try {
        const { user, token } = await buildAuthenticatedState(firebaseUser);
        setState({ user, token, isAuthenticated: true, isLoading: false });
      } catch {
        // Sesión de Firebase válida pero GET /me falló (backend caído, token
        // recién revocado, etc.) — se trata como no autenticado.
        setState({ user: null, token: null, isAuthenticated: false, isLoading: false });
      } finally {
        setIsInitializing(false);
      }
    });
    return unsubscribe;
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setState(s => ({ ...s, isLoading: true }));
    try {
      // Paso 2-3 del contrato: ubica el silo ANTES de autenticar. Siempre
      // responde 200 (no revela si la cuenta existe); si no hay match,
      // identityPlatformTenantId llega null y el sign-in falla solo, sin
      // trato especial que delate la diferencia.
      const { identityPlatformTenantId } = await tenantLookup(email);
      auth.tenantId = identityPlatformTenantId;

      const credential = await signInWithEmailAndPassword(auth, email, password);
      const { user, token } = await buildAuthenticatedState(credential.user);
      setState({ user, token, isAuthenticated: true, isLoading: false });
    } catch (err) {
      setState(s => ({ ...s, isLoading: false }));
      throw err;
    }
  }, []);

  const logout = useCallback(() => {
    // Limpia de inmediato (cubre también una sesión de loginDev, que nunca
    // tocó Firebase y por lo tanto no dispara onAuthStateChanged al salir).
    setState({ user: null, token: null, isAuthenticated: false, isLoading: false });
    void signOut(auth);
  }, []);

  const updateProfile = useCallback((data: Partial<Pick<User, 'name' | 'email' | 'phone'>>) => {
    // Edición optimista local: no hay PATCH /users todavía (501 a propósito
    // — docs/Paquete_Arranque_Frontend.md §8, hasta que el aprovisionamiento
    // de Identity Platform esté completo). FE-08 la conecta cuando exista.
    setState(s => (s.user ? { ...s, user: { ...s.user, ...data } } : s));
  }, []);

  const switchRole = import.meta.env.DEV
    ? (role: UserRole) => {
        setState(s => (s.user ? { ...s, user: { ...s.user, role } } : s));
      }
    : undefined;

  const loginDev = import.meta.env.DEV
    ? (role: UserRole) => {
        setState({ user: DEV_FIXTURES[role], token: 'dev-local-token', isAuthenticated: true, isLoading: false });
      }
    : undefined;

  return (
    <AuthContext.Provider value={{ ...state, isInitializing, login, logout, switchRole, loginDev, updateProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
